import { DAY_PART_SELECTIONS } from 'constants/menu';

import React, { FC, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { ItemAvailabilityStatus } from 'enums/menu';
import { LogUserErrorSeverity, useGetAndLogUserErrorMessage } from 'hooks/log-user-error-message';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';

import { Message, MessageIcon, Wrapper } from './styled';
import { IAvailabilityStatusProps } from './types';

export const getAvailabilityStatusMessage = ({
  availabilityStatus,
  dayParts,
  storeHasBurgersForBreakfast = false,
}: {
  availabilityStatus: ItemAvailabilityStatus;
  dayParts: string[];
  storeHasBurgersForBreakfast?: boolean;
}) => {
  if (availabilityStatus === ItemAvailabilityStatus.OUT_OF_DAYPART && !dayParts.length) {
    // fallback: if the dayParts aren't around,
    // show not available instead of a broken day part message
    return t('notAvailableAtStore');
  }

  const messageId: TranslationKey =
    ({
      [ItemAvailabilityStatus.AVAILABLE]: 'available',
      [ItemAvailabilityStatus.OUT_OF_DAYPART]: 'outOfDayparts',
      [ItemAvailabilityStatus.LIMITED_TIME_OFFER]: 'limitedTimeOffer',
      [ItemAvailabilityStatus.CART_EXTRA]: 'addFromCheckout',
      [ItemAvailabilityStatus.OUT_OF_MENU]: 'notAvailableAtStore',
      [ItemAvailabilityStatus.UNAVAILABLE]: 'notAvailableAtStore',
      [ItemAvailabilityStatus.STORE_NOT_SELECTED]: 'notAvailableAtStore',
      [ItemAvailabilityStatus.MAX_CART_QUANTITY_MET]: 'limitReached',
    } as const)[availabilityStatus] || 'notAvailableAtStore';

  const breakfastDayPart = DAY_PART_SELECTIONS.BREAKFAST_KEY.toLocaleLowerCase();
  const mainMenuDayPart = DAY_PART_SELECTIONS.MAIN_MENU_KEY.toLocaleLowerCase();
  const burgersForBreakfastDayPart = DAY_PART_SELECTIONS.BURGERS_FOR_BREAKFAST.toLocaleLowerCase();

  const dayPartsToCheck: string[] = [
    breakfastDayPart,
    mainMenuDayPart,
    ...(storeHasBurgersForBreakfast ? [burgersForBreakfastDayPart] : []),
  ];

  const dayPartsMessage = dayParts
    .filter(dayPart => dayPartsToCheck.includes(dayPart))
    .map(dayPart => (dayPart === mainMenuDayPart ? 'daytime' : dayPart))
    .join(', ');

  return dayPartsMessage ? t(messageId, { dayParts: dayPartsMessage }) : t(messageId);
};

/**
 *
 * AvailabilityStatus is used to display the status of a product
 * This will give a detailed error to guests explaining exactly why a product is unavailable
 *
 */
export const AvailabilityStatus: FC<React.PropsWithChildren<IAvailabilityStatusProps>> = ({
  availabilityStatus,
  name,
  dayParts = [],
  hideWhenAvailable = false,
}) => {
  const {
    store: { hasBurgersForBreakfast },
  } = useStoreContext();

  const message = useMemo(
    () =>
      getAvailabilityStatusMessage({
        availabilityStatus,
        dayParts,
        storeHasBurgersForBreakfast: hasBurgersForBreakfast ?? false,
      }),
    [availabilityStatus, dayParts, hasBurgersForBreakfast]
  );

  const getMessage = useGetAndLogUserErrorMessage({
    message,
    severity: LogUserErrorSeverity.Informational,
  });

  if (availabilityStatus === ItemAvailabilityStatus.AVAILABLE && hideWhenAvailable) {
    return null;
  }

  return message ? (
    <Wrapper>
      {availabilityStatus === ItemAvailabilityStatus.AVAILABLE ? (
        <MessageIcon
          variant="check"
          testID="item-available"
          {...hiddenAccessibilityPlatformProps}
        />
      ) : (
        <MessageIcon
          variant="error"
          testID="item-unavailable"
          {...hiddenAccessibilityPlatformProps}
        />
      )}
      <Message>{getMessage({ availabilityStatus, name })}</Message>
    </Wrapper>
  ) : null;
};
